/**
 * Settings — broker connection and account details.
 */

import { useState } from 'react';
import { Bell, Plug, Settings, Shield, User } from 'lucide-react';
import BrokerConnect from '@/features/auth/components/BrokerConnect';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateTime } from '@/lib/format';
import { Badge, Card, CardBody, CardHeader, EmptyState, PageHeader, Tabs } from '@/components/ui';

type SettingsTab = 'broker' | 'profile' | 'notifications' | 'security';

const TABS = [
  { id: 'broker' as const, label: 'Broker', icon: Plug },
  { id: 'profile' as const, label: 'Profile', icon: User },
  { id: 'notifications' as const, label: 'Notifications', icon: Bell },
  { id: 'security' as const, label: 'Security', icon: Shield },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('broker');
  const user = useAuthStore((s) => s.user);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader icon={Settings} title="Settings" description="Broker connection and account details" />

      <Tabs aria-label="Settings sections" items={TABS} value={activeTab} onChange={setActiveTab} />

      {activeTab === 'broker' && <BrokerConnect />}

      {activeTab === 'profile' && (
        <Card>
          <CardHeader title="Profile" description="Read-only — profile editing isn't exposed by the API yet" />
          <CardBody>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              {[
                { label: 'Username', value: user?.username },
                { label: 'Email', value: user?.email },
                { label: 'Role', value: user?.role },
                { label: 'Member since', value: formatDateTime(user?.created_at) },
              ].map((row) => (
                <div key={row.label} className="border-b border-line pb-3">
                  <dt className="text-xs text-fg-faint">{row.label}</dt>
                  <dd className="mt-0.5 text-sm text-fg">{row.value || '—'}</dd>
                </div>
              ))}
              <div className="border-b border-line pb-3">
                <dt className="text-xs text-fg-faint">Status</dt>
                <dd className="mt-1 flex gap-2">
                  <Badge tone={user?.is_active ? 'success' : 'neutral'}>
                    {user?.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge tone={user?.is_verified ? 'success' : 'warning'}>
                    {user?.is_verified ? 'Verified' : 'Unverified'}
                  </Badge>
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card>
          <EmptyState
            icon={Bell}
            title="Notifications aren't available yet"
            description="The backend has no notification endpoints, so there is nothing to configure here."
          />
        </Card>
      )}

      {activeTab === 'security' && (
        <Card>
          <EmptyState
            icon={Shield}
            title="Security settings aren't available yet"
            description="Password change and session management aren't exposed by the API. Sign out from the account menu to end this session."
          />
        </Card>
      )}
    </div>
  );
}
